import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { TFunction } from "i18next";
import type { TSelector } from "../../../i18n/types";

export const DIMENSION_TYPES = {
  KEYWORD_ID: "adv_keyword_id",
  TARGET_ID: "adv_target_id",
  TEXT: "text",
  TEXT_WITH_MATCH: "text,match_type",
} as const;

export const SEARCH_TERM_DIMENSIONS = {
  QUERY: "query",
  KEYWORD_TEXT: "keyword_text",
  KEYWORD_TEXT_WITH_MATCH: "keyword_text,match_type",
  QUERY_MATCH_KEYWORD: "keyword_text,query,match_type,campaign_id,adv_keyword_id",
  QUERY_MATCH_TARGET: "keyword_text,query,match_type,campaign_id,adv_target_id",
} as const;

type BidStateKey =
  | "Learning"
  | "Optimizing Up"
  | "Optimizing Down"
  | "Optimal"
  | "Fixed"
  | "Boosted"
  | "Channel Constrained"
  | "User Constrained"
  | "Budget Constrained"
  | "Dormant"
  | "Enrolled";

const TARGET_BID_STATE_KEYS: Record<number, BidStateKey> = {
  1: "Learning",
  2: "Optimizing Up",
  3: "Optimizing Down",
  4: "Optimal",
  5: "Fixed",
  6: "Boosted",
  7: "Channel Constrained",
  8: "User Constrained",
  9: "Budget Constrained",
  10: "Dormant",
  11: "Enrolled",
};

const BID_STATE_SELECTORS: Record<BidStateKey, TSelector> = {
  Learning: ($) => $.str.bid_state["Learning"],
  "Optimizing Up": ($) => $.str.bid_state["Optimizing Up"],
  "Optimizing Down": ($) => $.str.bid_state["Optimizing Down"],
  Optimal: ($) => $.str.bid_state["Optimal"],
  Fixed: ($) => $.str.bid_state["Fixed"],
  Boosted: ($) => $.str.bid_state["Boosted"],
  "Channel Constrained": ($) => $.str.bid_state["Channel Constrained"],
  "User Constrained": ($) => $.str.bid_state["User Constrained"],
  "Budget Constrained": ($) => $.str.bid_state["Budget Constrained"],
  Dormant: ($) => $.str.bid_state["Dormant"],
  Enrolled: ($) => $.str.bid_state["Enrolled"],
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getTargetBidStateLabel = (t: TFunction<any, any>, value: number): string => {
  const key = TARGET_BID_STATE_KEYS[value];
  return key ? t(BID_STATE_SELECTORS[key]) : "";
};

export const useTargetBidStateOptions = () => {
  const { t } = useTranslation("str");
  return useMemo(
    () =>
      Object.entries(TARGET_BID_STATE_KEYS).map(([value, key]) => ({
        value: Number(value),
        label: t(BID_STATE_SELECTORS[key]),
      })),
    [t],
  );
};

export const DEFAULT_TARGET_REPORT_SELECTED_COLUMNS = [
  "text",
  "bid_resolved",
  "bid_trend_latest",
  "bid_trend_slope",
  "match_type",
  "state",
  "ad_spend",
  "ad_sales",
  "orders",
  "acos",
  "conversion_rate",
  "cpc",
  "clicks",
  "impressions",
  "ctr",
];

export const DEFAULT_SEARCH_TERM_REPORT_SELECTED_COLUMNS = [
  "keyword_text",
  "query",
  "bid",
  "match_type",
  "state",
  "ad_spend",
  "ad_sales",
  "orders",
  "acos",
  "roas",
  "conversion_rate",
  "cpc",
  "clicks",
  "impressions",
  "ctr",
  "adv_keyword_ids_count",
  "adv_target_ids_count",
];
